import React, { useState, useEffect, useCallback } from 'react';
import {
  HiOutlineDatabase, HiOutlineDownload, HiOutlinePlusCircle,
  HiOutlineSwitchVertical, HiOutlineCloudDownload, HiOutlineArchive,
  HiOutlineExclamationCircle, HiOutlineServer
} from 'react-icons/hi';
import ConfirmModal from '../components/ConfirmModal';

const API_BASE = process.env.REACT_APP_API_URL || '';
const REFRESH_INTERVAL = 30000;
const MONTHS_FR = ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'];

function formatBackupName(filename) {
  const match = filename.match(/ds5_(?:staging_)?(\d{4})-(\d{2})-(\d{2})_(\d{2})-(\d{2})/);
  if (!match) return filename;
  const [, y, mo, d, h, mi] = match;
  return `${parseInt(d)} ${MONTHS_FR[parseInt(mo) - 1]} ${y} à ${h}:${mi}`;
}

function isStaging(filename) {
  return filename.includes('staging_');
}

export default function StagingBackupsPage({ token, stagingStatus, onNavigate }) {
  const [prodBackups, setProdBackups] = useState([]);
  const [stagingBackups, setStagingBackups] = useState([]);
  const [syncStatus, setSyncStatus] = useState(null);
  const [stagingRunning, setStagingRunning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [message, setMessage] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchData = useCallback(async () => {
    // Skip API calls when staging is down — SSH times out
    if (stagingStatus === 'down') {
      setLoading(false);
      return;
    }
    try {
      const [prodBackupsRes, stagingBackupsRes, syncRes, statusRes] = await Promise.all([
        fetch(`${API_BASE}/api/monitoring/backups`, { headers }).catch(() => null),
        fetch(`${API_BASE}/api/staging/backup/list`, { headers }).catch(() => null),
        fetch(`${API_BASE}/api/staging/sync/status`, { headers }).catch(() => null),
        fetch(`${API_BASE}/api/staging/status`, { headers }).catch(() => null),
      ]);
      if (prodBackupsRes?.ok) {
        const data = await prodBackupsRes.json();
        setProdBackups(data.backups || []);
      }
      if (stagingBackupsRes?.ok) {
        const data = await stagingBackupsRes.json();
        setStagingBackups(data.backups || []);
      }
      if (syncRes?.ok) setSyncStatus(await syncRes.json());
      if (statusRes?.ok) {
        const data = await statusRes.json();
        setStagingRunning(data.ec2?.state === 'running');
      }
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [token, stagingStatus]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchData]);

  const doAction = async (url, method = 'POST', body = null) => {
    setActionLoading(url);
    setMessage(null);
    try {
      const res = await fetch(`${API_BASE}${url}`, {
        method,
        headers,
        body: body ? JSON.stringify(body) : null,
      });
      const data = await res.json();
      setMessage({ type: res.ok ? 'success' : 'error', text: data.message || data.error });
      await fetchData();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setActionLoading('');
    }
  };

  return (
    <div className="fade-in">
      <div className="page-title">
        <HiOutlineDatabase className="title-icon" style={{ color: 'var(--accent-blue)' }} />
        Sauvegardes Staging
      </div>

      {/* Message banner */}
      {message && (
        <div
          className="card"
          style={{
            marginBottom: 16,
            borderLeft: `4px solid ${message.type === 'success' ? 'var(--accent-green)' : 'var(--accent-red)'}`,
            padding: '12px 20px',
          }}
        >
          {message.text}
        </div>
      )}

      {/* When staging is down, show ONLY the banner — nothing else */}
      {stagingStatus === 'down' && (
        <div className="card" style={{
          borderLeft: '4px solid var(--accent-red)',
          padding: '16px 20px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <HiOutlineExclamationCircle style={{ color: 'var(--accent-red)', fontSize: 24, flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Serveur staging arrêté</div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                Le serveur staging est actuellement éteint. Les sauvegardes ne sont pas accessibles.
              </div>
            </div>
            {onNavigate && (
              <button
                className="btn btn-primary"
                onClick={() => onNavigate('staging')}
                style={{ padding: '6px 14px', fontSize: 12, whiteSpace: 'nowrap' }}
              >
                <HiOutlineServer style={{ verticalAlign: 'middle', marginRight: 4 }} />
                Démarrer le serveur
              </button>
            )}
          </div>
        </div>
      )}

      {/* Synchro Data info — managed by scheduler in Contrôle Staging */}
      {stagingStatus !== 'down' && <>
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <div className="card-title"><HiOutlineSwitchVertical className="icon" /> Synchro Prod → Staging</div>
          <span className={`badge ${syncStatus?.active ? 'healthy' : ''}`} style={{ fontSize: 11 }}>
            {syncStatus?.active ? 'Activée' : 'Désactivée'}
          </span>
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          {syncStatus?.active
            ? 'La synchro automatique est activée. Le dernier backup prod (02:00 UTC) est restauré sur staging à 03:00 UTC.'
            : 'La synchro est désactivée.'}
          <span style={{ marginLeft: 8 }}>
            Gérer dans{' '}
            {onNavigate ? (
              <button
                className="btn btn-outline"
                style={{ padding: '2px 8px', fontSize: 11, verticalAlign: 'middle' }}
                onClick={() => onNavigate('staging')}
              >
                Contrôle Staging
              </button>
            ) : 'Contrôle Staging'}
          </span>
        </div>
        {syncStatus?.lastResult && (
          <div style={{ marginTop: 8, fontSize: 12, padding: '6px 10px', borderRadius: 6, background: 'var(--bg-tertiary)' }}>
            Dernière synchro :{' '}
            <span style={{ color: syncStatus.lastResult.status === 'success' ? 'var(--accent-green)' : 'var(--accent-red)', fontWeight: 600 }}>
              {syncStatus.lastResult.status === 'success' ? '✓ Réussie' : '✗ Échouée'}
            </span>
            {syncStatus.lastResult.backup && <> — {syncStatus.lastResult.backup}</>}
            {syncStatus.lastResult.duration && <> ({syncStatus.lastResult.duration}s)</>}
          </div>
        )}
      </div>

      {/* Section 1: Backup Staging (propre au staging) */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <div className="card-title"><HiOutlineArchive className="icon" /> Backup Staging</div>
          <button
            className="btn btn-primary"
            style={{ padding: '8px 16px', fontSize: 13, fontWeight: 600 }}
            disabled={!stagingRunning || !!actionLoading}
            onClick={() => setConfirmModal({
              title: 'Créer un backup staging',
              message: 'Une sauvegarde complète de la base de données staging sera créée sur le serveur staging.',
              confirmLabel: 'Créer le backup',
              danger: false,
              onConfirm: () => { setConfirmModal(null); doAction('/api/staging/backup/create'); },
            })}
          >
            {actionLoading === '/api/staging/backup/create' ? <span className="spin">⟳</span> : <HiOutlinePlusCircle />}
            Nouveau backup
          </button>
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
          Sauvegardes de la base de données stockées sur le serveur staging (<code>/home/ubuntu/backups/</code>).
          <br />Aucun impact sur la production.
        </div>
        {loading ? (
          <div className="empty-state">Chargement…</div>
        ) : stagingBackups.length === 0 ? (
          <div className="empty-state">Aucun backup staging disponible</div>
        ) : (
          stagingBackups.map(b => (
            <div className="backup-row" key={b.filename}>
              <div className="backup-info">
                <div className="backup-filename" style={{ fontFamily: 'inherit' }}>
                  <span className="badge" style={{ fontSize: 10, padding: '2px 6px', marginRight: 8, background: 'var(--accent-blue)', color: '#fff' }}>STAGING</span>
                  {formatBackupName(b.filename)}
                </div>
                <div className="backup-details">{b.filename} — {b.sizeMB} Mo</div>
              </div>
              <button
                className="btn btn-primary btn-sm"
                disabled={!stagingRunning || !!actionLoading}
                onClick={() => {
                  setConfirmModal({
                    title: 'Restaurer ce backup staging',
                    message: `Restaurer "${formatBackupName(b.filename)}" sur le staging ?`,
                    warning: 'Cette action remplacera toutes les données de la base staging par ce backup staging.',
                    confirmLabel: 'Restaurer',
                    danger: true,
                    onConfirm: () => {
                      setConfirmModal(null);
                      setActionLoading(b.filename);
                      doAction('/api/staging/backup/restore-staging', 'POST', { filename: b.filename });
                    },
                  });
                }}
              >
                {actionLoading === b.filename ? <span className="spin">⟳</span> : <HiOutlineDownload />}
                Restaurer
              </button>
            </div>
          ))
        )}
      </div>

      {/* Section 2: Restaurer depuis la production */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <div className="card-title"><HiOutlineCloudDownload className="icon" /> Restaurer depuis la Production</div>
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
          Envoyer un backup depuis le serveur de production vers le staging pour le restaurer.
          <br />Le fichier sera transféré (prod → staging) puis importé dans la base staging. La prod n'est pas affectée.
        </div>
        {loading ? (
          <div className="empty-state">Chargement…</div>
        ) : prodBackups.length === 0 ? (
          <div className="empty-state">Aucun backup production disponible</div>
        ) : (
          prodBackups.map(b => (
            <div className="backup-row" key={b.filename}>
              <div className="backup-info">
                <div className="backup-filename" style={{ fontFamily: 'inherit' }}>
                  <span className="badge" style={{ fontSize: 10, padding: '2px 6px', marginRight: 8, background: 'var(--accent-yellow)', color: '#000' }}>PROD</span>
                  {formatBackupName(b.filename)}
                </div>
                <div className="backup-details">{b.filename} — {b.sizeMB} Mo</div>
              </div>
              <button
                className="btn btn-primary btn-sm"
                disabled={!stagingRunning || !!actionLoading}
                onClick={() => {
                  setConfirmModal({
                    title: 'Restaurer un backup production',
                    message: `Restaurer le backup production "${formatBackupName(b.filename)}" sur l'environnement staging ?`,
                    warning: 'Toutes les données staging actuelles seront remplacées par les données de production.',
                    confirmLabel: 'Restaurer vers staging',
                    danger: true,
                    onConfirm: () => {
                      setConfirmModal(null);
                      setActionLoading(b.filename);
                      doAction('/api/staging/backup/restore', 'POST', { filename: b.filename });
                    },
                  });
                }}
              >
                {actionLoading === b.filename ? <span className="spin">⟳</span> : <HiOutlineDownload />}
                Restaurer → Staging
              </button>
            </div>
          ))
        )}
      </div>

      {/* Confirm modal */}
      <ConfirmModal
        open={!!confirmModal}
        title={confirmModal?.title}
        message={confirmModal?.message}
        warning={confirmModal?.warning}
        confirmLabel={confirmModal?.confirmLabel}
        danger={confirmModal?.danger}
        onConfirm={confirmModal?.onConfirm}
        onCancel={() => setConfirmModal(null)}
      />
      </>}
    </div>
  );
}
